// LOLER Due dates (Session SC7A): the first tab of the LOLER and SAFed hub. Every live lift subject to a LOLER thorough examination, kept together by
// customer, with its site, its next examination due, who arranges it (the customer by default, ELE only where the customer record says so) and
// the three-band status, soonest first. Filterable by customer and band.
//
// READ ONLY, derived live from the lifts' own LOLER clocks (GET /api/loler/due): nothing is stored, nothing is sent and nothing is chased from
// here. The band is the Lifts register's own (red overdue or due within 30 days, amber 31 to 90 days, green beyond), and a lift with no date
// recorded says so rather than reading green. 50 at a time; cards on a phone.
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import type { LolerDueBand, LolerDueRow } from "../../lib/api";
import { Pager } from "../../components/Pager";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill, type PillTone } from "../../components/StatusPill";
import { RecordPicker } from "../../components/RecordPicker";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { useListSearch } from "../../lib/useListSearch";
import { useServerPage } from "../../lib/useServerPage";
import { formatDate } from "../../lib/format";

const PAGE_SIZE = 50;

// A filter chip: quiet, with a green outline only on the one that is on (the Defects page's filterButtonClass pattern).
function chipClass(isActive: boolean): string {
  const active = "border-ele-green bg-ele-inset text-ele-green";
  return `rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${isActive ? active : "border-ele-purple-accent text-ele-body hover:text-ele-green"}`;
}

// The band's words and tone. The pill always carries the words, so colour is never the only signal.
const BAND: Record<LolerDueBand, { word: string; tone: PillTone }> = {
  red: { word: "Overdue or due within 30 days", tone: "red" },
  amber: { word: "Due in 31 to 90 days", tone: "amber" },
  green: { word: "More than 90 days away", tone: "green" },
  none: { word: "No date recorded", tone: "grey" },
};
const SHORT: Record<LolerDueBand, string> = { red: "Due soon or overdue", amber: "Due in 31 to 90 days", green: "More than 90 days", none: "No date recorded" };
const ORDER: LolerDueBand[] = ["red", "amber", "green", "none"];

function arrangedWords(r: LolerDueRow): string {
  return r.arrangedBy === "ELE" ? "ELE arranges it" : "The customer arranges it";
}

export function LolerDuePage() {
  const [mobileCards] = useState(initialMobileLayout);
  const [params, setParams] = useSearchParams();
  const { search, setSearch } = useListSearch();
  const q = (params.get("q") ?? "").trim();
  const customerId = params.get("customerId") ?? "";
  const bandParam = params.get("band");
  const band: LolerDueBand | "" = bandParam === "red" || bandParam === "amber" || bandParam === "green" || bandParam === "none" ? bandParam : "";

  function setFilter(key: string, value: string) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  }

  const queryKey = JSON.stringify({ q, customerId, band });
  const page = useServerPage(queryKey, (at) =>
    api.loler.duePage({ ...(customerId ? { customerId } : {}), ...(band ? { band } : {}), ...(q ? { q } : {}), sort: "grouped", limit: PAGE_SIZE, offset: at }),
  );
  const rows = page.data?.items ?? [];
  const total = page.data?.total ?? 0;
  const counts = page.data?.counts;
  const narrowed = Boolean(q || customerId || band);

  const filters = (
    <div className="flex flex-wrap items-center gap-2">
      <div role="group" aria-label="Status band" className="flex flex-wrap gap-2">
        {ORDER.map((b) => (
          <button
            key={b}
            type="button"
            aria-pressed={band === b}
            onClick={() => setFilter("band", band === b ? "" : b)}
            className={chipClass(band === b)}
          >
            {SHORT[b]}
            {counts ? ` (${counts[b]})` : ""}
          </button>
        ))}
      </div>
      <div className="min-w-[12rem]">
        <RecordPicker kind="customer" id="loler-due-customer" ariaLabel="Customer" value={customerId} placeholder="Any customer" onChange={(id) => setFilter("customerId", id)} />
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="LOLER due dates"
        subtitle="Every lift that needs a thorough examination, kept together by customer, soonest first. The customer arranges their own examination unless the customer record says ELE does."
      />
      <ListToolbar
        className="mb-3"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Lift, site or customer"
        searchLabel="Search lifts"
        filters={filters}
        resultCount={narrowed ? { shown: rows.length, total, noun: "lifts" } : undefined}
        onClear={
          narrowed
            ? () => {
                setSearch("");
                setParams(new URLSearchParams(), { replace: true });
              }
            : undefined
        }
      />

      {page.loading ? (
        <Loader />
      ) : page.error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the due dates. Please try again in a moment.
        </p>
      ) : rows.length === 0 ? (
        <EmptyState role={narrowed ? "status" : undefined} message={narrowed ? "No lifts match these filters." : "No lifts are subject to a LOLER examination yet."} />
      ) : mobileCards ? (
        <MobileCardList label="LOLER due dates">
          {rows.map((r, i) => (
            <li key={r.assetId} className="list-none">
              {(i === 0 || rows[i - 1]!.customerId !== r.customerId) && (
                <p className="mb-1 mt-2 font-menu text-xs uppercase tracking-widest text-ele-blue">{r.customerName}</p>
              )}
              <ul>
                <MobileListCard
                  to={`/office/lifts/${encodeURIComponent(r.assetId)}`}
                  primary={<span className="font-heading">{r.eleNumber ?? "Lift"}</span>}
                  secondary={r.siteName}
                  meta={`${r.due ? `Due ${formatDate(r.due)}` : "No date recorded"} | ${arrangedWords(r)}`}
                  right={<StatusPill word={BAND[r.band].word} tone={BAND[r.band].tone} />}
                />
              </ul>
            </li>
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "12%" }} />
              <col style={{ width: "24%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <th className="px-3 py-2">Lift</th>
                <th className="px-3 py-2">Site</th>
                <th className="px-3 py-2">Next examination due</th>
                <th className="px-3 py-2">Last examined</th>
                <th className="px-3 py-2">Who arranges it</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <GroupedRow key={r.assetId} r={r} startsGroup={i === 0 || rows[i - 1]!.customerId !== r.customerId} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!page.loading && !page.error && (
        <div className={page.refreshing ? "opacity-60" : undefined}>
          <Pager offset={page.offset} total={total} shown={rows.length} pageSize={PAGE_SIZE} onOffset={page.setOffset} noun={["lift", "lifts"]} />
        </div>
      )}
    </div>
  );
}

function GroupedRow({ r, startsGroup }: { r: LolerDueRow; startsGroup: boolean }) {
  return (
    <>
      {startsGroup && (
        <tr className="border-t border-ele-purple-accent bg-ele-surface-soft">
          <th colSpan={6} scope="colgroup" className="px-3 py-2 text-left font-heading text-ele-strong">
            <Link to={`/office/customers/${encodeURIComponent(r.customerId)}`} className="hover:text-ele-green hover:underline">
              {r.customerName}
            </Link>
          </th>
        </tr>
      )}
      <tr className="border-t border-ele-purple-accent">
        <td className="px-3 py-2 align-top font-heading">
          <Link to={`/office/lifts/${encodeURIComponent(r.assetId)}`} className="text-ele-strong hover:text-ele-green hover:underline">
            {r.eleNumber ?? "Lift"}
          </Link>
        </td>
        <td className="px-3 py-2 align-top">
          <Link to={`/office/sites/${encodeURIComponent(r.siteId)}`} className="text-ele-green underline-offset-2 hover:underline">
            {r.siteName}
          </Link>
        </td>
        <td className="whitespace-nowrap px-3 py-2 align-top">{r.due ? formatDate(r.due) : <span className="text-ele-body opacity-60">Not recorded</span>}</td>
        <td className="whitespace-nowrap px-3 py-2 align-top">{r.lastExamined ? formatDate(r.lastExamined) : <span className="text-ele-body opacity-60">Not recorded</span>}</td>
        <td className="px-3 py-2 align-top">{arrangedWords(r)}</td>
        <td className="px-3 py-2 align-top">
          <StatusPill word={BAND[r.band].word} tone={BAND[r.band].tone} />
        </td>
      </tr>
    </>
  );
}
